import dayjs, { Dayjs } from 'dayjs';

import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { PricingSourceType } from 'pricing/enums';
import { ICostSourceFormValue, ICustomerQuotePart } from 'quotes/types';

import { isVendorQuoteWithoutValidUntil, TOneTimeSpaRow } from './oneTimeSpa';

export type TCostValidityDate = Dayjs | string | null | undefined;

export const COST_VALID_SINCE_FIELD_NAME = 'costSource.validSince';
export const COST_VALID_UNTIL_FIELD_NAME = 'costSource.validUntil';

// The row holds "YYYY-MM-DD HH:MM:SS" (valid until at 23:59:59); only the day is the user's.
export const parseCostValidityDate = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }

  const date = dayjs(value.substring(0, 10));

  return date.isValid() ? date : null;
};

/**
 * MM/DD/YYYY, or '' to clear: the BE refuses any other format with a 422, and multipart would post
 * a Dayjs as an object.
 */
export const formatCostValidityDateForServer = (value: TCostValidityDate): string => {
  if (!value) {
    return '';
  }

  const date = dayjs(value);

  return date.isValid() ? date.format(SERVER_FORMAT_DATE_LONG) : '';
};

/**
 * Only a typed cost's dates are the user's: a pick, a list price and a vendor quote cost keep their
 * own on the same row.
 */
export const getTypedCostValidityDefaults = (
  quotePart?: Pick<
    ICustomerQuotePart,
    'costSourceType' | 'costSourceValidSince' | 'costSourceValidUntil'
  > | null
): Pick<ICostSourceFormValue, 'validSince' | 'validUntil'> => {
  if (PricingSourceType.CUSTOM !== quotePart?.costSourceType) {
    return { validSince: null, validUntil: null };
  }

  return {
    validSince: parseCostValidityDate(quotePart?.costSourceValidSince),
    validUntil: parseCostValidityDate(quotePart?.costSourceValidUntil),
  };
};

// The Valid until the user gave a vendor quote cost whose vendor quote has none.
export const getVendorQuoteValidityDefaults = (
  quotePart?: TOneTimeSpaRow | null
): Pick<ICostSourceFormValue, 'validSince' | 'validUntil'> => ({
  validSince: null,
  validUntil: isVendorQuoteWithoutValidUntil(quotePart)
    ? parseCostValidityDate(quotePart.costSourceValidUntil)
    : null,
});

// A saved line's form starts from the dates the user gave its cost, whichever kind of cost it is.
export const getCostValidityDefaults = (
  quotePart?: TOneTimeSpaRow | null
): Pick<ICostSourceFormValue, 'validSince' | 'validUntil'> =>
  PricingSourceType.CUSTOM === quotePart?.costSourceType
    ? getTypedCostValidityDefaults(quotePart)
    : getVendorQuoteValidityDefaults(quotePart);

// The same day is allowed; an incomplete range has nothing to compare.
export const isCostValidityRangeValid = (
  validSince: TCostValidityDate,
  validUntil: TCostValidityDate
): boolean => {
  if (!validSince || !validUntil) {
    return true;
  }

  const since = dayjs(validSince);
  const until = dayjs(validUntil);

  if (!since.isValid() || !until.isValid()) {
    return true;
  }

  return !until.isBefore(since, 'day');
};

export const clearCostValidityDates = (setValue: (name: string, value: null) => void): void => {
  setValue(COST_VALID_SINCE_FIELD_NAME, null);
  setValue(COST_VALID_UNTIL_FIELD_NAME, null);
};
